'use client'

import * as React from 'react'
import { Tabs } from './index'
type Pane = {
  menuItem: string
  render: () => React.ReactNode
}

interface TabComponentProps {
  className?: string
  container?: ({
    children,
    className,
  }: {
    children?: React.ReactNode
    className?: string
  }) => JSX.Element | React.ReactNode
  footer?: React.ReactNode
  header?: React.ReactNode
  onTabClick?: (index: number) => void
  defaultActiveIndex?: number
  panes: Pane[]
  variant?: 'default' | 'outline'
  fullWidth?: boolean
  scrollable?: boolean
}

const Tab = ({
  panes,
  className,
  container: ContainerComponent = ({ children }) => children,
  footer,
  header,
  onTabClick,
  defaultActiveIndex = 0,
  variant = 'default',
  fullWidth,
  scrollable,
  ...props
}: TabComponentProps) => {
  const [activeIndex, setActiveIndex] = React.useState(defaultActiveIndex)
  const [syncedIndex, setSyncedIndex] = React.useState(defaultActiveIndex)

  // Callers drive the tab from their own state, so a changed prop wins over the last click.
  if (syncedIndex !== defaultActiveIndex) {
    setSyncedIndex(defaultActiveIndex)
    setActiveIndex(defaultActiveIndex)
  }

  const handleTabClick = (index: number) => {
    onTabClick?.(index)
    setActiveIndex(index)
  }
  return (
    <Tabs
      className={className}
      value={panes[activeIndex]?.menuItem}
      onValueChange={(value) => {
        const newIndex = panes.findIndex((pane) => pane.menuItem === value)
        if (newIndex !== -1) {
          handleTabClick(newIndex)
        }
      }}
      {...props}
    >
      {header && <div>{header}</div>}

      <Tabs.List
        variant={variant}
        fullWidth={fullWidth}
        className={`flex flex-wrap md:flex-nowrap gap-2 ${scrollable ? 'w-full overflow-x-auto' : ' w-fit'}`}
      >
        {panes.map((pane, index) => (
          <Tabs.Trigger
            variant={variant}
            key={`${index}-${pane.menuItem}`}
            value={pane.menuItem}
            fullWidth={fullWidth}
          >
            {pane.menuItem}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      <ContainerComponent>
        {panes.map((pane, index) => (
          <Tabs.Content
            variant={variant}
            key={`${index}-${pane.menuItem}`}
            value={pane.menuItem}
          >
            {pane.render()}
          </Tabs.Content>
        ))}
      </ContainerComponent>

      {footer && <div>{footer}</div>}
    </Tabs>
  )
}

export { Tab }
